import { useEffect, useMemo } from "react"

import { faCheck, faExternalLink } from "@fortawesome/pro-regular-svg-icons"
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
  Stack,
  CircularProgress,
  ListItem,
  List,
  ListItemIcon,
  Link,
  Alert,
} from "@mui/material"
import { FaIcon } from "@skistarab/mui-theme"
import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { ReadMoreButton } from "@/components"
import StyledHelperText from "@/components/StyledHelperText"
import { useIsSummer } from "@/hooks"
import useWebAdminTranslations from "@/hooks/useWebAdminTranslations"

import { SKIPASS_KEYS, SUBSCRIPTION_KEYS } from "./translationKeys"
import { PassHolderFormData } from "../../schema"

export type ValidatedInsurance = {
  name: string
  shortDescription: string
  fullDescription: string
  price: number
  existingPrice: number
  currencySymbol: string
  analyticsName: string
  analyticsId: string
}

type InsuranceFieldProps = {
  insuranceData?: ValidatedInsurance | null
  isLoading?: boolean
  ageGroup: string
  bookingLengthInDays: number
  priceFetchError?: string
  isSubscriptionProduct: boolean
  isEditMode?: boolean
}

const InsuranceField = ({
  insuranceData,
  ageGroup,
  bookingLengthInDays,
  isLoading,
  isSubscriptionProduct,
  priceFetchError,
  isEditMode = false,
}: InsuranceFieldProps) => {
  const { t } = useTranslation()
  const isSummer = useIsSummer()
  const { t: tWebAdminTranslations } = useWebAdminTranslations()
  const { control, setValue } = useFormContext<PassHolderFormData>()

  const isPriceZero = insuranceData?.price === 0

  const translationKeys = isSubscriptionProduct
    ? SUBSCRIPTION_KEYS
    : SKIPASS_KEYS

  const tInsurance = (
    key: keyof Omit<typeof SKIPASS_KEYS, "INCLUDES">
  ): string => {
    return tWebAdminTranslations(translationKeys[key])
  }

  const hasFetchError = !!priceFetchError

  const priceDisplayText = useMemo(() => {
    if (!insuranceData || insuranceData.price === undefined) return null

    const hasExisting = (insuranceData.existingPrice ?? 0) > 0

    if (isPriceZero) {
      return `${t("passHolderDetails.form.insuranceIncluded")}, ${ageGroup}${
        !isSubscriptionProduct
          ? ` ${bookingLengthInDays} ${t("passHolderDetails.form.insurancePriceDays", { count: bookingLengthInDays })}`
          : ""
      }`
    }

    const prefix = hasExisting
      ? `${t("passHolderDetails.form.completeInsurance")}, `
      : ""
    const daysText = !isSubscriptionProduct
      ? `${bookingLengthInDays} ${t("passHolderDetails.form.insurancePriceDays", { count: bookingLengthInDays })} `
      : ""

    return `${prefix}${ageGroup} ${daysText}${insuranceData.price} ${insuranceData.currencySymbol}`
  }, [
    insuranceData,
    isPriceZero,
    ageGroup,
    bookingLengthInDays,
    isSubscriptionProduct,
    t,
  ])

  const shouldShowPriceText = priceDisplayText !== null && !isLoading
  const shouldShowPlaceholderText =
    insuranceData?.price === undefined && !hasFetchError && !isLoading

  useEffect(() => {
    if (isPriceZero) {
      setValue("insurance", true, { shouldValidate: false })
    } else if (isEditMode) {
      if ((insuranceData?.existingPrice ?? 0) === 0) {
        setValue("insurance", false, { shouldValidate: false })
      } else {
        // Should only happend when user is able to top up an already existing insurance
        setValue("insurance", true, { shouldValidate: false })
      }
    }
  }, [isPriceZero, isEditMode, insuranceData?.existingPrice, setValue])

  if (hasFetchError) {
    return (
      <Alert severity="info">
        {t("passHolderDetails.form.insuranceFetchError")}
      </Alert>
    )
  }

  const isRadioGroupDisabled = isLoading || !insuranceData?.price

  return (
    <Controller
      name="insurance"
      control={control}
      render={({ field, fieldState: { error: formError } }) => (
        <Box
          border="1px solid"
          borderColor="grey.300"
          borderRadius="4px"
          padding={2}
          sx={{ "&:last-child": { pb: 0 } }}
        >
          <FormControl
            component="fieldset"
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <FormLabel
              component="legend"
              sx={{
                typography: "body2",
                fontWeight: "bold",
                color: "text.primary",
              }}
            >
              {tInsurance("HEADER")}
            </FormLabel>
            {(shouldShowPriceText || isLoading) && (
              <Box minHeight="24px" display="flex" alignItems="center">
                {shouldShowPriceText && (
                  <Typography variant="body3">{priceDisplayText}</Typography>
                )}
                {isLoading && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={18} />
                    <Typography variant="body3" fontStyle="italic">
                      {t("passHolderDetails.form.insurancePriceloading")}
                    </Typography>
                  </Stack>
                )}
              </Box>
            )}
            {!isPriceZero && (
              <RadioGroup
                value={field.value?.toString() ?? ""}
                onChange={(e) => {
                  if (!isRadioGroupDisabled) {
                    field.onChange(e.target.value === "true")
                  }
                }}
                onBlur={field.onBlur}
                name={field.name}
              >
                <Stack direction="row" spacing={1} my={1} marginLeft={-1.5}>
                  <FormControlLabel
                    value="true"
                    control={<Radio disabled={isRadioGroupDisabled} />}
                    label={
                      <Typography variant="body2" fontWeight="bold">
                        {t(
                          "passHolderDetails.form.insuranceRadioButtonLabelYes"
                        )}
                      </Typography>
                    }
                    disabled={isRadioGroupDisabled}
                    sx={{
                      "&.Mui-disabled": {
                        color: "text.secondary",
                        opacity: 1,
                      },
                    }}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio disabled={isRadioGroupDisabled} />}
                    label={
                      <Typography variant="body2" fontWeight="bold">
                        {t(
                          "passHolderDetails.form.insuranceRadioButtonLabelNo"
                        )}
                      </Typography>
                    }
                    disabled={isRadioGroupDisabled}
                    sx={{
                      "&.Mui-disabled": {
                        color: "text.secondary",
                        opacity: 1,
                      },
                    }}
                  />
                </Stack>
              </RadioGroup>
            )}
            {shouldShowPlaceholderText && (
              <Box mb={1}>
                <StyledHelperText
                  helperText={t(
                    "passHolderDetails.form.enterSkierDetailsForPriceCalculation",
                    { context: isSummer ? "summer" : undefined }
                  )}
                  severity="warning"
                />
              </Box>
            )}
          </FormControl>

          <List disablePadding>
            {translationKeys.INCLUDES.map((key) => (
              <ListItem key={key} disableGutters sx={{ gap: 1 }}>
                <ListItemIcon sx={{ minWidth: "auto" }}>
                  <FaIcon icon={faCheck} fontSize="inherit" />
                </ListItemIcon>
                <Typography variant="body3">
                  {tWebAdminTranslations(key)}
                </Typography>
              </ListItem>
            ))}
          </List>
          <Box>
            <ReadMoreButton
              modalHeadline={tInsurance("MODAL_TITLE")}
              buttonLabel={tInsurance("READMORE")}
            >
              <Typography variant="body2">
                {tInsurance("MODAL_BODY")}
              </Typography>
              <Link
                href={tInsurance("TERMS_LINK")}
                target="_blank"
                rel="noopener noreferrer"
                fontSize="medium"
              >
                <Box display="flex" alignItems="center" gap={1} py={1}>
                  <FaIcon icon={faExternalLink} fontSize="inherit" />
                  <Typography variant="body3">
                    {tInsurance("TERMS_TITLE")}
                  </Typography>
                </Box>
              </Link>
            </ReadMoreButton>
          </Box>
          {formError?.message && (
            <StyledHelperText helperText={formError.message} severity="error" />
          )}
        </Box>
      )}
    />
  )
}

export default InsuranceField
